import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Crown, BadgeCheck, ShoppingBag, Loader2 } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

// Public, unauthenticated preview of a shared product/post with a register CTA.
// Registered users are sent straight to the exact in-app page.
export default function SharePreview({ kind }) {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/${kind}/${id}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
    // Remember intended destination so the app deep-links after sign-up.
    try { sessionStorage.setItem("rc_after_auth", `/app?${kind}=${id}`); } catch {}
  }, [kind, id]);

  if (loading || user === null) {
    return <div className="min-h-screen grid place-items-center bg-[var(--rc-bg)]"><Loader2 className="animate-spin text-[var(--rc-gold)]" /></div>;
  }
  if (user) return <Navigate to={`/app?${kind}=${id}`} replace />;

  const price = data && (data.promo_price ?? data.price);

  return (
    <div className="min-h-screen bg-[var(--rc-bg)] text-[var(--rc-text)] flex flex-col">
      <header className="flex items-center gap-2 px-5 py-4 border-b border-[var(--rc-border)]">
        <Crown className="text-[var(--rc-gold)]" size={22} />
        <span className="font-heading text-lg">RoyalChat Nova</span>
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-5 py-8" data-testid="share-preview">
        {err ? (
          <div className="text-center py-16 text-[var(--rc-muted)]" data-testid="share-preview-error">{t("share_not_found")}</div>
        ) : !data ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[var(--rc-gold)]" /></div>
        ) : kind === "product" ? (
          <div className="rounded-3xl border border-[var(--rc-border)] bg-[var(--rc-surface)] overflow-hidden">
            <div className="aspect-square bg-[var(--rc-bg)] grid place-items-center">
              {data.image ? <img src={data.image} alt="" className="h-full w-full object-cover" /> : <ShoppingBag size={40} className="text-[var(--rc-muted)]" />}
            </div>
            <div className="p-5">
              {data.shop_name && <div className="text-xs text-[var(--rc-gold)] flex items-center gap-1 mb-1">{data.shop_name}{data.shop_verified && <BadgeCheck size={13} />}</div>}
              <h1 className="text-xl font-heading">{data.title}</h1>
              <div className="text-2xl font-semibold text-[var(--rc-gold)] mt-1">{data.currency} {price}</div>
              {data.description && <p className="text-sm text-[var(--rc-muted)] mt-3 line-clamp-4">{data.description}</p>}
            </div>
          </div>
        ) : (
          <div className="rounded-3xl border border-[var(--rc-border)] bg-[var(--rc-surface)] overflow-hidden">
            <div className="flex items-center gap-3 p-4">
              <div className="h-11 w-11 rounded-full bg-[var(--rc-bg)] overflow-hidden grid place-items-center text-[var(--rc-gold)] font-semibold">
                {data.author_avatar ? <img src={data.author_avatar} alt="" className="h-full w-full object-cover" /> : (data.author_name || "?")[0]}
              </div>
              <div className="font-medium">{data.author_name}</div>
            </div>
            {data.text && <p className="px-4 pb-3 whitespace-pre-wrap">{data.text}</p>}
            {data.media && data.media.url && (data.media.type || "").startsWith("video")
              ? <video src={data.media.url} className="w-full max-h-96 object-cover" controls />
              : data.media && data.media.url ? <img src={data.media.url} alt="" className="w-full max-h-96 object-cover" /> : null}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-gold)]/10 p-5 text-center">
          <p className="text-sm text-[var(--rc-text)] mb-3">{t("share_join_cta")}</p>
          <Link to="/register" data-testid="share-join-btn" className="inline-block rounded-full bg-[var(--rc-gold)] text-black font-semibold px-8 py-3">{t("share_join_btn")}</Link>
          <div className="mt-3 text-xs text-[var(--rc-muted)]">{t("already_account")} <Link to="/login" className="text-[var(--rc-gold)] underline">{t("login")}</Link></div>
        </div>
      </main>
    </div>
  );
}
